import { gql } from '@apollo/client';

// ==================== QUERIES ====================

export const GET_CONVERSATIONS = gql`
  query GetConversations($userId: String!, $page: Int, $limit: Int) {
    getConversations(userId: $userId, page: $page, limit: $limit) {
      status
      message
      response {
        id
        type
        name
        participants {
          userId
          userName
          joinedAt
          lastReadAt
          muted
        }
        createdBy
        lastMessage {
          text
          senderId
          senderName
          sentAt
        }
        linkedEntity {
          entityType
          entityId
          entityName
          entityMeta {
            status
            stage
            assignedTo
            priority
          }
        }
        category
        autoCreated
        isActive
        createdAt
        updatedAt
        unreadCount
      }
    }
  }
`;

export const GET_MESSAGES = gql`
  query GetMessages($conversationId: String!, $page: Int, $limit: Int) {
    getMessages(conversationId: $conversationId, page: $page, limit: $limit) {
      status
      message
      response {
        id
        conversationId
        senderId
        senderName
        type
        content
        fileUrl
        fileName
        fileMimeType
        replyTo
        replyToMessage {
          id
          senderId
          senderName
          content
          type
        }
        reactions {
          emoji
          userId
          userName
          createdAt
        }
        pinnedBy
        pinnedAt
        forwardedFrom {
          messageId
          conversationId
          senderName
        }
        readBy {
          userId
          readAt
        }
        deliveredTo {
          userId
          deliveredAt
        }
        status
        mentions {
          type
          value
          displayName
        }
        embeddedAction {
          actionType
          entityType
          entityId
          payload
          executedBy
          executedAt
          result
          resultMessage
        }
        botIntent
        auditLinked
        editedAt
        isDeleted
        createdAt
      }
      hasMore
      totalCount
    }
  }
`;

export const SEARCH_MESSAGES = gql`
  query SearchMessages($userId: String!, $query: String!, $conversationId: String, $page: Int, $limit: Int) {
    searchMessages(userId: $userId, query: $query, conversationId: $conversationId, page: $page, limit: $limit) {
      status
      message
      response {
        id
        conversationId
        senderId
        senderName
        type
        content
        createdAt
      }
      hasMore
      totalCount
    }
  }
`;

export const GET_UNREAD_COUNT = gql`
  query GetUnreadCount($userId: String!) {
    getUnreadCount(userId: $userId) {
      status
      message
      totalUnread
    }
  }
`;

export const GET_USER_PRESENCES = gql`
  query GetUserPresences($userIds: [String]!) {
    getUserPresences(userIds: $userIds) {
      status
      message
      response {
        userId
        status
        lastSeen
        customStatus
      }
    }
  }
`;

// ==================== MUTATIONS ====================

export const CREATE_CONVERSATION = gql`
  mutation CreateConversation($type: String!, $name: String, $participantIds: [String]!, $createdBy: String!) {
    createConversation(type: $type, name: $name, participantIds: $participantIds, createdBy: $createdBy) {
      status
      message
      response {
        id
        type
        name
        participants {
          userId
          userName
          joinedAt
          lastReadAt
          muted
        }
        createdBy
        lastMessage {
          text
          senderId
          senderName
          sentAt
        }
        isActive
        createdAt
        updatedAt
        unreadCount
      }
    }
  }
`;

export const SEND_MESSAGE = gql`
  mutation SendMessage(
    $conversationId: String!
    $senderId: String!
    $senderName: String!
    $type: String
    $content: String
    $fileUrl: String
    $fileName: String
    $fileMimeType: String
    $replyTo: String
    $tempId: String
  ) {
    sendMessage(
      conversationId: $conversationId
      senderId: $senderId
      senderName: $senderName
      type: $type
      content: $content
      fileUrl: $fileUrl
      fileName: $fileName
      fileMimeType: $fileMimeType
      replyTo: $replyTo
      tempId: $tempId
    ) {
      status
      message
      response {
        id
        conversationId
        senderId
        senderName
        type
        content
        fileUrl
        fileName
        fileMimeType
        replyTo
        readBy {
          userId
          readAt
        }
        deliveredTo {
          userId
          deliveredAt
        }
        status
        isDeleted
        createdAt
      }
    }
  }
`;

export const MARK_MESSAGES_READ = gql`
  mutation MarkMessagesRead($conversationId: String!, $userId: String!) {
    markMessagesRead(conversationId: $conversationId, userId: $userId) {
      status
      message
    }
  }
`;

export const ADD_PARTICIPANTS = gql`
  mutation AddParticipants($conversationId: String!, $participantIds: [String]!, $addedBy: String!) {
    addParticipants(conversationId: $conversationId, participantIds: $participantIds, addedBy: $addedBy) {
      status
      message
      response {
        id
        type
        name
        participants {
          userId
          userName
          joinedAt
          lastReadAt
          muted
        }
        createdBy
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

export const REMOVE_PARTICIPANT = gql`
  mutation RemoveParticipant($conversationId: String!, $userId: String!, $removedBy: String!) {
    removeParticipant(conversationId: $conversationId, userId: $userId, removedBy: $removedBy) {
      status
      message
      response {
        id
        type
        name
        participants {
          userId
          userName
          joinedAt
          lastReadAt
          muted
        }
        createdBy
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_GROUP_NAME = gql`
  mutation UpdateGroupName($conversationId: String!, $name: String!, $updatedBy: String!) {
    updateGroupName(conversationId: $conversationId, name: $name, updatedBy: $updatedBy) {
      status
      message
      response {
        id
        type
        name
        updatedAt
      }
    }
  }
`;

export const TOGGLE_REACTION = gql`
  mutation ToggleReaction($messageId: String!, $conversationId: String!, $emoji: String!, $userId: String!, $userName: String!) {
    toggleReaction(messageId: $messageId, conversationId: $conversationId, emoji: $emoji, userId: $userId, userName: $userName) {
      status
      message
      response {
        id
        reactions {
          emoji
          userId
          userName
          createdAt
        }
      }
    }
  }
`;

export const EDIT_MESSAGE = gql`
  mutation EditMessage($messageId: String!, $conversationId: String!, $content: String!, $userId: String!) {
    editMessage(messageId: $messageId, conversationId: $conversationId, content: $content, userId: $userId) {
      status
      message
      response {
        id
        content
        editedAt
      }
    }
  }
`;

export const DELETE_MESSAGE = gql`
  mutation DeleteMessage($messageId: String!, $conversationId: String!, $userId: String!) {
    deleteMessage(messageId: $messageId, conversationId: $conversationId, userId: $userId) {
      status
      message
      response {
        id
        isDeleted
        content
      }
    }
  }
`;

export const PIN_MESSAGE = gql`
  mutation PinMessage($messageId: String!, $conversationId: String!, $userId: String!) {
    pinMessage(messageId: $messageId, conversationId: $conversationId, userId: $userId) {
      status
      message
      response {
        id
        pinnedBy
        pinnedAt
      }
    }
  }
`;

export const UNPIN_MESSAGE = gql`
  mutation UnpinMessage($messageId: String!, $conversationId: String!) {
    unpinMessage(messageId: $messageId, conversationId: $conversationId) {
      status
      message
      response {
        id
        pinnedBy
        pinnedAt
      }
    }
  }
`;

export const FORWARD_MESSAGE = gql`
  mutation ForwardMessage($originalMessageId: String!, $targetConversationId: String!, $senderId: String!, $senderName: String!) {
    forwardMessage(originalMessageId: $originalMessageId, targetConversationId: $targetConversationId, senderId: $senderId, senderName: $senderName) {
      status
      message
      response {
        id
        conversationId
        senderId
        senderName
        type
        content
        fileUrl
        fileName
        fileMimeType
        forwardedFrom {
          messageId
          conversationId
          senderName
        }
        createdAt
      }
    }
  }
`;

// ==================== BUSINESS CONTEXT MUTATIONS ====================

export const CREATE_LINKED_CONVERSATION = gql`
  mutation CreateLinkedConversation(
    $type: String!
    $name: String
    $participantIds: [String]!
    $createdBy: String!
    $entityType: String!
    $entityId: String!
    $entityName: String
    $category: String
  ) {
    createLinkedConversation(
      type: $type
      name: $name
      participantIds: $participantIds
      createdBy: $createdBy
      entityType: $entityType
      entityId: $entityId
      entityName: $entityName
      category: $category
    ) {
      status
      message
      response {
        id
        type
        name
        participants { userId joinedAt lastReadAt muted }
        createdBy
        lastMessage { text senderId senderName sentAt }
        linkedEntity {
          entityType entityId entityName
          entityMeta { status stage assignedTo priority }
        }
        category
        isActive
        createdAt
        updatedAt
        unreadCount
      }
    }
  }
`;

export const EXECUTE_EMBEDDED_ACTION = gql`
  mutation ExecuteEmbeddedAction(
    $messageId: String!
    $conversationId: String!
    $actionType: String!
    $entityType: String!
    $entityId: String!
    $payload: String
    $userId: String!
  ) {
    executeEmbeddedAction(
      messageId: $messageId
      conversationId: $conversationId
      actionType: $actionType
      entityType: $entityType
      entityId: $entityId
      payload: $payload
      userId: $userId
    ) {
      status
      message
      response {
        id
        embeddedAction {
          actionType entityType entityId
          result resultMessage executedBy executedAt
        }
      }
    }
  }
`;
